import { useMutation } from '@tanstack/react-query';
import { ChevronDown, Languages } from 'lucide-react';
import type { UiLocale } from '@his/shared';
import { patch } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { LANGUAGE_NAMES } from '@/lib/country';
import { rememberUiLocale, useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useToast } from './ui/toast';
import { ActionMenu, ActionMenuItem, ActionMenuLabel } from './ui/action-menu';

export function LanguageSwitch() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const { t } = useTranslation('shell');
  const save = useMutation({
    mutationFn: (locale: UiLocale) => patch('/auth/me/preferences', { locale }),
    onSuccess: async (_saved, locale) => {
      rememberUiLocale(locale);
      await refreshUser();
    },
    onError: (error) => toast.error(t('menu.language'), describeError(error)),
  });

  const options = user?.uiLocales ?? [];
  if (!user || user.isPlatformUser || options.length < 2) return null;
  const active = user.uiLocale ?? 'en';

  return (
    <ActionMenu
      label={t('menu.language')}
      trigger={
        <button
          type="button"
          aria-label={t('menu.language')}
          disabled={save.isPending}
          className={cn(
            'flex h-9 items-center gap-1.5 rounded-full px-2.5 text-sm text-muted-foreground transition-colors focus-ring',
            'hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground disabled:opacity-60',
          )}
        >
          <Languages className="h-4 w-4 shrink-0" aria-hidden />
          <span className="hidden md:block" lang={active}>
            {LANGUAGE_NAMES[active]}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </button>
      }
    >
      <ActionMenuLabel>{t('menu.language')}</ActionMenuLabel>
      {options.map((locale) => (
        <ActionMenuItem key={locale} selected={locale === active} onSelect={() => save.mutate(locale)}>
          <span lang={locale}>{LANGUAGE_NAMES[locale]}</span>
        </ActionMenuItem>
      ))}
    </ActionMenu>
  );
}
